import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Card,
  Col,
  Collapse,
  Grid,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  theme,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PageHeader from '../../components/common/PageHeader';
import { abrangenciaColumns } from '../../components/common/AbrangenciaColumns';
import { useAbrangencia } from '../../hooks/useAbrangencia';
import { useStatusDashboardOverview } from '../../hooks/useStatusDashboardOverview';
import {
  CONSECUTIVE_ALERT_KEY,
  DASHBOARD_PANEL_ERROS_CONSECUTIVOS,
} from '../../constants/dashboardAlerts';
import {
  fetchDemorandoRetorno,
  fetchErrosConsecutivos,
  fetchErrosDiaAnterior,
  type DemorandoRetornoRow,
  type ErrosConsecutivosRow,
  type ErrosDiaAnteriorRow,
} from '../../services/statusDashboardService';
import {
  DEFAULT_KEY,
  DASHBOARD_OVERVIEW_COL_SPAN,
  ERROR_FILTER_OPTIONS,
  PAGE_SIZE,
  PERIOD_OPTIONS,
  STATUS_DASHBOARD_BY_KEY_DESCRIPTION,
  STATUS_DASHBOARD_CATEGORIA_DESCRIPTION,
  STATUS_DASHBOARD_DEMORANDO_RETORNO_DESCRIPTION,
  STATUS_DASHBOARD_ERROS_CONSECUTIVOS_DESCRIPTION,
  STATUS_DASHBOARD_ERROS_DIA_ANTERIOR_DESCRIPTION,
  STATUS_DASHBOARD_SUMMARY_DESCRIPTION,
  STATUS_DASHBOARD_TOP_ERROS_DESCRIPTION,
  STATUS_DASHBOARD_TRIBUNAL_HEALTH_DESCRIPTION,
  TOP_ERROR_COLORS,
  type SummaryStatusKey,
  type TribunalHealthKey,
} from '../Dashboard/shared/constants';
import {
  buildConsecutiveSessionKey,
  buildErrorsSessionKey,
  buildKeyOptions,
  buildSlowSessionKey,
  formatDuration,
  formatIntervalDays,
  getApiErrorMessage,
  isPanelOpen,
} from '../Dashboard/shared/utils';
import { getSectionPanelStyle, useDashboardRenderers } from '../Dashboard/shared/useDashboardRenderers';

const { useBreakpoint } = Grid;

const DashboardStatusContent: React.FC = () => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: abrangenciaData } = useAbrangencia();
  const {
    data: overviewData,
    loading: overviewLoading,
    error: overviewError,
  } = useStatusDashboardOverview();
  const [summaryHoveredKey, setSummaryHoveredKey] = useState<SummaryStatusKey | null>(null);
  const [tribunalHealthHoveredKey, setTribunalHealthHoveredKey] = useState<TribunalHealthKey | null>(null);

  const [consecutivePage, setConsecutivePage] = useState(1);
  const [consecutiveData, setConsecutiveData] = useState<ErrosConsecutivosRow[]>([]);
  const [consecutiveTotal, setConsecutiveTotal] = useState(0);
  const [consecutiveLoading, setConsecutiveLoading] = useState(false);
  const [consecutiveKey, setConsecutiveKey] = useState<string>(DEFAULT_KEY);
  const [consecutiveKeyOptions, setConsecutiveKeyOptions] = useState(() => buildKeyOptions());

  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsComparison, setErrorsComparison] = useState<'gte' | 'lt'>('gte');
  const [errorsData, setErrorsData] = useState<ErrosDiaAnteriorRow[]>([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [errorsKey, setErrorsKey] = useState<string>(DEFAULT_KEY);
  const [errorsKeyOptions, setErrorsKeyOptions] = useState(() => buildKeyOptions());

  const [period, setPeriod] = useState<'previous' | 'current'>('previous');
  const [slowPage, setSlowPage] = useState(1);
  const [slowData, setSlowData] = useState<DemorandoRetornoRow[]>([]);
  const [slowTotal, setSlowTotal] = useState(0);
  const [slowLoading, setSlowLoading] = useState(false);
  const [slowKey, setSlowKey] = useState<string>(DEFAULT_KEY);
  const [slowKeyOptions, setSlowKeyOptions] = useState(() => buildKeyOptions());

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [consecutiveOpen, setConsecutiveOpen] = useState(false);
  const [slowOpen, setSlowOpen] = useState(false);
  const [consecutiveLoaded, setConsecutiveLoaded] = useState(false);
  const [errorsLoaded, setErrorsLoaded] = useState(false);
  const [slowLoaded, setSlowLoaded] = useState(false);

  const [consecutiveFromCache, setConsecutiveFromCache] = useState(false);
  const [consecutiveCachedAt, setConsecutiveCachedAt] = useState<string | null>(null);
  const [errorsFromCache, setErrorsFromCache] = useState(false);
  const [errorsCachedAt, setErrorsCachedAt] = useState<string | null>(null);
  const [slowFromCache, setSlowFromCache] = useState(false);
  const [slowCachedAt, setSlowCachedAt] = useState<string | null>(null);

  const consecutiveSessionKeyRef = useRef<string | null>(null);
  const consecutivePanelRef = useRef<HTMLDivElement | null>(null);
  const errorsSessionKeyRef = useRef<string | null>(null);
  const slowSessionKeyRef = useRef<string | null>(null);

  const {
    renderCollapseHeaderExtra,
    renderCollapseLabel,
    renderTopErrorCard,
    renderKeyStackedBarCard,
    renderTribunalHealthCard,
    renderSummaryCard,
    renderTableCard,
  } = useDashboardRenderers(isMobile);

  const loadConsecutive = useCallback(async (
    page: number,
    key: string,
    options?: { forceRefresh?: boolean },
  ) => {
    const sessionKey = buildConsecutiveSessionKey(page, key);
    setConsecutiveLoading(true);
    try {
      const resp = await fetchErrosConsecutivos({
        key,
        page,
        pageSize: PAGE_SIZE,
        forceRefresh: options?.forceRefresh,
      });
      if (!resp.success) throw new Error(resp.message || 'Erro ao buscar erros consecutivos');
      setConsecutiveData(resp.data || []);
      setConsecutiveTotal(resp.total || 0);
      setConsecutivePage(resp.page || page);
      setConsecutiveFromCache(resp.fromCache === true);
      setConsecutiveCachedAt(resp.fromCache ? resp.cachedAt ?? null : null);
      setConsecutiveLoaded(true);
      const availableKeys = buildKeyOptions(resp.availableKeys);
      setConsecutiveKeyOptions(availableKeys);
      if (!availableKeys.some((option) => option.value === key)) {
        setConsecutiveKey(DEFAULT_KEY);
      }
      consecutiveSessionKeyRef.current = sessionKey;
      setErrorMessage(null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Erro ao carregar erros consecutivos');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      setConsecutiveLoading(false);
    }
  }, []);

  const loadYesterdayErrors = useCallback(async (
    page: number,
    comparison: 'gte' | 'lt',
    key: string,
    options?: { forceRefresh?: boolean },
  ) => {
    const sessionKey = buildErrorsSessionKey(page, comparison, key);
    setErrorsLoading(true);
    try {
      const resp = await fetchErrosDiaAnterior({
        page,
        pageSize: PAGE_SIZE,
        comparison,
        threshold: 12,
        key,
        forceRefresh: options?.forceRefresh,
      });
      if (!resp.success) throw new Error(resp.message || 'Erro ao buscar erros do dia anterior');
      setErrorsData(resp.data || []);
      setErrorsTotal(resp.total || 0);
      setErrorsPage(resp.page || page);
      setErrorsFromCache(resp.fromCache === true);
      setErrorsCachedAt(resp.fromCache ? resp.cachedAt ?? null : null);
      setErrorsLoaded(true);
      const availableKeys = buildKeyOptions(resp.availableKeys);
      setErrorsKeyOptions(availableKeys);
      if (!availableKeys.some((option) => option.value === key)) {
        setErrorsKey(DEFAULT_KEY);
      }
      errorsSessionKeyRef.current = sessionKey;
      setErrorMessage(null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Erro ao carregar erros do dia anterior');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      setErrorsLoading(false);
    }
  }, []);

  const loadSlowReturn = useCallback(async (
    page: number,
    selectedPeriod: 'previous' | 'current',
    key: string,
    options?: { forceRefresh?: boolean },
  ) => {
    const sessionKey = buildSlowSessionKey(page, selectedPeriod, key);
    setSlowLoading(true);
    try {
      const resp = await fetchDemorandoRetorno({
        period: selectedPeriod,
        key,
        page,
        pageSize: PAGE_SIZE,
        forceRefresh: options?.forceRefresh,
      });
      if (!resp.success) throw new Error(resp.message || 'Erro ao buscar demoras no retorno');
      setSlowData(resp.data || []);
      setSlowTotal(resp.total || 0);
      setSlowPage(resp.page || page);
      setSlowFromCache(resp.fromCache === true);
      setSlowCachedAt(resp.fromCache ? resp.cachedAt ?? null : null);
      setSlowLoaded(true);
      const availableKeys = buildKeyOptions(resp.availableKeys);
      setSlowKeyOptions(availableKeys);
      if (!availableKeys.some((option) => option.value === key)) {
        setSlowKey(DEFAULT_KEY);
      }
      slowSessionKeyRef.current = sessionKey;
      setErrorMessage(null);
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Erro ao carregar demoras no retorno');
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      setSlowLoading(false);
    }
  }, []);

  const openConsecutivePanelFromAlert = useCallback(() => {
    setConsecutiveKey(CONSECUTIVE_ALERT_KEY);
    setConsecutivePage(1);
    setConsecutiveOpen(true);
    consecutiveSessionKeyRef.current = null;

    window.requestAnimationFrame(() => {
      consecutivePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useEffect(() => {
    if (!overviewError) return;
    setErrorMessage(overviewError);
    message.error(overviewError);
  }, [overviewError]);

  useEffect(() => {
    if (searchParams.get('panel') !== DASHBOARD_PANEL_ERROS_CONSECUTIVOS) return;

    openConsecutivePanelFromAlert();
    setSearchParams({}, { replace: true });
  }, [openConsecutivePanelFromAlert, searchParams, setSearchParams]);

  useEffect(() => {
    const handleOpenConsecutivePanel = () => {
      openConsecutivePanelFromAlert();
    };

    window.addEventListener('dashboard:open-erros-consecutivos', handleOpenConsecutivePanel);
    return () => {
      window.removeEventListener('dashboard:open-erros-consecutivos', handleOpenConsecutivePanel);
    };
  }, [openConsecutivePanelFromAlert]);

  useEffect(() => {
    if (!consecutiveOpen) return;
    const sessionKey = buildConsecutiveSessionKey(consecutivePage, consecutiveKey);
    if (consecutiveSessionKeyRef.current === sessionKey) return;
    loadConsecutive(consecutivePage, consecutiveKey);
  }, [consecutiveOpen, consecutiveKey, consecutivePage, loadConsecutive]);

  useEffect(() => {
    if (!errorsOpen) return;
    const sessionKey = buildErrorsSessionKey(errorsPage, errorsComparison, errorsKey);
    if (errorsSessionKeyRef.current === sessionKey) return;
    loadYesterdayErrors(errorsPage, errorsComparison, errorsKey);
  }, [errorsOpen, errorsComparison, errorsKey, errorsPage, loadYesterdayErrors]);

  useEffect(() => {
    if (!slowOpen) return;
    const sessionKey = buildSlowSessionKey(slowPage, period, slowKey);
    if (slowSessionKeyRef.current === sessionKey) return;
    loadSlowReturn(slowPage, period, slowKey);
  }, [slowOpen, period, slowKey, slowPage, loadSlowReturn]);

  const handlePanelToggle = useCallback((
    panelKey: 'consecutive' | 'slow' | 'yesterday',
    keys: string | string[],
    sessionKeyRef: React.MutableRefObject<string | null>,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const isOpen = isPanelOpen(keys, panelKey);
    if (!isOpen) {
      sessionKeyRef.current = null;
    }
    setOpen(isOpen);
  }, []);
  const handleConsecutiveKeyChange = (value: string) => {
    setConsecutiveKey(value);
    setConsecutivePage(1);
    consecutiveSessionKeyRef.current = null;
  };

  const handlePeriodChange = (value: 'previous' | 'current') => {
    setPeriod(value);
    setSlowPage(1);
    slowSessionKeyRef.current = null;
  };

  const handleSlowKeyChange = (value: string) => {
    setSlowKey(value);
    setSlowPage(1);
    slowSessionKeyRef.current = null;
  };

  const handleErrorsComparisonChange = (value: 'gte' | 'lt') => {
    setErrorsComparison(value);
    setErrorsPage(1);
    errorsSessionKeyRef.current = null;
  };

  const handleErrorsKeyChange = (value: string) => {
    setErrorsKey(value);
    setErrorsPage(1);
    errorsSessionKeyRef.current = null;
  };

  const handleConsecutiveRefresh = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    consecutiveSessionKeyRef.current = null;
    loadConsecutive(consecutivePage, consecutiveKey, { forceRefresh: true });
  }, [consecutivePage, consecutiveKey, loadConsecutive]);

  const handleYesterdayErrorsRefresh = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    errorsSessionKeyRef.current = null;
    loadYesterdayErrors(errorsPage, errorsComparison, errorsKey, { forceRefresh: true });
  }, [errorsPage, errorsComparison, errorsKey, loadYesterdayErrors]);

  const handleSlowReturnRefresh = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    slowSessionKeyRef.current = null;
    loadSlowReturn(slowPage, period, slowKey, { forceRefresh: true });
  }, [slowPage, period, slowKey, loadSlowReturn]);

  const consecutiveColumns: ColumnsType<ErrosConsecutivosRow> = [
    ...(abrangenciaColumns as ColumnsType<ErrosConsecutivosRow>),
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      ellipsis: true,
    },
    {
      title: 'Dias',
      dataIndex: 'tempo_em_erro',
      key: 'tempo_em_erro',
      width: 100,
      render: (value: unknown) => formatIntervalDays(value),
    },
  ];

  const errorsColumns: ColumnsType<ErrosDiaAnteriorRow> = [
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 80,
      render: (value: number) => <Tag color="error">{value}</Tag>,
    },
    ...(abrangenciaColumns as ColumnsType<ErrosDiaAnteriorRow>),
  ];

  const renderStatusTag = (record: DemorandoRetornoRow) => {
    const normalizedStatus = String(record.status || (record.is_pending ? 'pending' : '')).trim().toLowerCase();

    if (normalizedStatus === 'pending') return <Tag color="processing">pending</Tag>;
    if (normalizedStatus === 'error') return <Tag color="error">error</Tag>;
    if (normalizedStatus === 'warning') return <Tag color="warning">warning</Tag>;
    if (normalizedStatus === 'success') return <Tag color="success">success</Tag>;

    return <Tag>{normalizedStatus || '-'}</Tag>;
  };

  const slowColumns: ColumnsType<DemorandoRetornoRow> = [
    {
      title: 'Duração',
      dataIndex: 'total',
      key: 'total',
      width: 110,
      render: (value: number, record) => (
        <Space size={4}>
          <Tag color={record.is_pending ? 'orange' : 'default'}>
            {formatDuration(Number(value))}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (_value: string, record) => renderStatusTag(record),
    },
    ...(abrangenciaColumns as ColumnsType<DemorandoRetornoRow>),
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PageHeader title="Dashboard Status" />

        {errorMessage && (
          <Alert
            type="error"
            showIcon
            message="Erro ao carregar dados"
            description={errorMessage}
            closable
            onClose={() => setErrorMessage(null)}
          />
        )}

        <div style={getSectionPanelStyle(token, isMobile)}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[16, 16]} justify="center">
              <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                {renderSummaryCard(
                  overviewData?.statusSummary,
                  overviewLoading,
                  summaryHoveredKey,
                  setSummaryHoveredKey,
                  STATUS_DASHBOARD_SUMMARY_DESCRIPTION,
                )}
              </Col>

              <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                {renderTribunalHealthCard(
                  overviewData?.tribunalHealth,
                  overviewLoading,
                  tribunalHealthHoveredKey,
                  setTribunalHealthHoveredKey,
                  STATUS_DASHBOARD_TRIBUNAL_HEALTH_DESCRIPTION,
                )}
              </Col>

              <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                {renderKeyStackedBarCard(
                  overviewData?.byKey,
                  overviewLoading,
                  STATUS_DASHBOARD_BY_KEY_DESCRIPTION,
                )}
              </Col>

              <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                {renderTopErrorCard(
                  'Por categoria',
                  overviewData?.topErrors?.abrangencias,
                  TOP_ERROR_COLORS.abrangencias,
                  overviewLoading,
                  STATUS_DASHBOARD_CATEGORIA_DESCRIPTION,
                  { orientation: 'vertical' },
                )}
              </Col>

              <Col {...DASHBOARD_OVERVIEW_COL_SPAN} style={{ display: 'flex' }}>
                {renderTopErrorCard(
                  'Top 5 erros',
                  overviewData?.topErrors?.searchPlataformas,
                  TOP_ERROR_COLORS.searchPlataformas,
                  overviewLoading,
                  STATUS_DASHBOARD_TOP_ERROS_DESCRIPTION,
                )}
              </Col>
            </Row>

            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div ref={consecutivePanelRef}>
                <Collapse
                  activeKey={consecutiveOpen ? ['consecutive'] : []}
                  onChange={(keys) => handlePanelToggle('consecutive', keys, consecutiveSessionKeyRef, setConsecutiveOpen)}
                  items={[
                    {
                      key: 'consecutive',
                      label: renderCollapseLabel(
                        'Erros consecutivos',
                        renderCollapseHeaderExtra(
                          consecutiveLoaded ? consecutiveFromCache : undefined,
                          consecutiveCachedAt,
                          consecutiveLoading,
                          consecutiveLoaded ? handleConsecutiveRefresh : undefined,
                        ),
                        STATUS_DASHBOARD_ERROS_CONSECUTIVOS_DESCRIPTION,
                      ),
                      children: renderTableCard(
                        'Status - Erros consecutivos',
                        (
                          <Select
                            value={consecutiveKey}
                            onChange={handleConsecutiveKeyChange}
                            options={consecutiveKeyOptions}
                            style={{ minWidth: isMobile ? 110 : 120 }}
                            size="small"
                          />
                        ),
                        consecutiveLoading,
                        consecutiveData,
                        consecutiveTotal,
                        consecutivePage,
                        consecutiveColumns,
                        (p) => loadConsecutive(p, consecutiveKey),
                      ),
                    },
                  ]}
                />
              </div>

              <Collapse
                activeKey={slowOpen ? ['slow'] : []}
                onChange={(keys) => handlePanelToggle('slow', keys, slowSessionKeyRef, setSlowOpen)}
                items={[
                  {
                    key: 'slow',
                    label: renderCollapseLabel(
                      'Demorando no retorno',
                      renderCollapseHeaderExtra(
                        slowLoaded ? slowFromCache : undefined,
                        slowCachedAt,
                        slowLoading,
                        slowLoaded ? handleSlowReturnRefresh : undefined,
                      ),
                      STATUS_DASHBOARD_DEMORANDO_RETORNO_DESCRIPTION,
                    ),
                    children: renderTableCard(
                      'Status - Demorando no retorno',
                      (
                        <Space size={8}>
                          <Select
                            value={period}
                            onChange={handlePeriodChange}
                            options={PERIOD_OPTIONS}
                            style={{ minWidth: isMobile ? 140 : 170 }}
                            size="small"
                          />
                          <Select
                            value={slowKey}
                            onChange={handleSlowKeyChange}
                            options={slowKeyOptions}
                            style={{ minWidth: isMobile ? 110 : 120 }}
                            size="small"
                          />
                        </Space>
                      ),
                      slowLoading,
                      slowData,
                      slowTotal,
                      slowPage,
                      slowColumns,
                      (p) => loadSlowReturn(p, period, slowKey),
                    ),
                  },
                ]}
              />

              <Collapse
                activeKey={errorsOpen ? ['yesterday'] : []}
                onChange={(keys) => handlePanelToggle('yesterday', keys, errorsSessionKeyRef, setErrorsOpen)}
                items={[
                  {
                    key: 'yesterday',
                    label: renderCollapseLabel(
                      'Erros do dia anterior',
                      renderCollapseHeaderExtra(
                        errorsLoaded ? errorsFromCache : undefined,
                        errorsCachedAt,
                        errorsLoading,
                        errorsLoaded ? handleYesterdayErrorsRefresh : undefined,
                      ),
                      STATUS_DASHBOARD_ERROS_DIA_ANTERIOR_DESCRIPTION,
                    ),
                    children: renderTableCard(
                      'Status - Erros dia anterior',
                      (
                        <Space size={8}>
                          <Select
                            value={errorsComparison}
                            onChange={handleErrorsComparisonChange}
                            options={ERROR_FILTER_OPTIONS}
                            style={{ minWidth: isMobile ? 140 : 150 }}
                            size="small"
                          />
                          <Select
                            value={errorsKey}
                            onChange={handleErrorsKeyChange}
                            options={errorsKeyOptions}
                            style={{ minWidth: isMobile ? 110 : 120 }}
                            size="small"
                          />
                        </Space>
                      ),
                      errorsLoading,
                      errorsData,
                      errorsTotal,
                      errorsPage,
                      errorsColumns,
                      (p) => loadYesterdayErrors(p, errorsComparison, errorsKey),
                    ),
                  },
                ]}
              />
            </Space>
          </Space>
        </div>
      </Space>
    </Card>
  );
};

const DashboardStatus: React.FC = () => (
  <DashboardStatusContent />
);

export default DashboardStatus;
